import Reflux from 'reflux';
import { OrderedMap, Map } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import FormMixin from 'flux/stores/FormMixin';
import EventShowListActions from 'flux/actions/EventShowListActions';
import ShowFormActions from 'flux/actions/ShowFormActions';
import ShowEditFormActions from 'flux/actions/ShowEditFormActions';
import ShowCSVFormActions from 'flux/actions/ShowCSVFormActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import ToastActions from 'flux/actions/ToastActions';

let showCreated = ShowFormActions.create.completed;
let showCSVCreated = ShowCSVFormActions.createFromCsv.completed;
let showSaved = ShowEditFormActions.save.completed;

const LIST_ARCHIVES = 'showArchives';
const LIST_SHOWS = 'shows';
const LIST_TEMPLATES = 'showTemplates';
const LIST_SAVED = 'savedShows';

/**
 * EventShowListStore
 *
 * Maintain event show list state
 *
 */
export default Reflux.createStore({
  listenables: [EventShowListActions, RealTimeActions],

  mixins: [BaseMixin, FormMixin],

  init() {
    this.setState(
      Map({
        shows: OrderedMap(),
        showTemplates: OrderedMap(),
        savedShows: OrderedMap(),
        showArchives: OrderedMap(),
      }),
    );

    this.listenTo(showCreated, 'onCreateCompleted');
    this.listenTo(showCSVCreated, 'onCSVImportCompleted');
    this.listenTo(showSaved, 'onSaveCompleted');
  },

  onShowCreatedRealTime(teamId, eventId, show) {
    this._appendNewShow(show);
  },

  onShowMutatedRealTime(showId, changes) {
    this._updateShow(showId, Map(changes), LIST_SHOWS);
  },

  onToggleEdit(show) {
    this.setStateAndTrigger(this.getState().set('editingShow', show));
  },

  onCreateCompleted(show) {
    this._appendNewShow(show);
  },

  onCSVImportCompleted(show) {
    this._appendNewShow(show);
  },

  onSaveCompleted(show) {
    this._appendNewShow(show);
    this._quitEditing();
    ToastActions.saved();
  },

  onCancelEdit() {
    this._quitEditing();
  },

  _quitEditing() {
    this.setStateAndTrigger(this.getState().delete('editingShow'));
  },

  onPopulateCompleted(shows) {
    let allShows = OrderedMap(shows.map((show) => [show._id, Map(show)]));

    this.setStateAndTrigger(
      this.getState()
        .set(
          LIST_ARCHIVES,
          _sort(allShows.filter((show) => show.get('archived'))),
        )
        .set(
          LIST_SHOWS,
          _sort(
            allShows.filter(
              (show) =>
                show.get('saved') !== true &&
                show.get('template') !== true &&
                !show.get('archived'),
            ),
          ),
        )
        .set(
          LIST_SAVED,
          _sort(
            allShows.filter(
              (show) => show.get('saved') === true && !show.get('archived'),
            ),
          ),
        )
        .set(
          LIST_TEMPLATES,
          _sort(
            allShows.filter(
              (show) => show.get('template') === true && !show.get('archived'),
            ),
          ),
        ),
    );
  },

  onPopulateForGuestCompleted(shows) {
    let allShows = OrderedMap(shows.map((show) => [show._id, Map(show)]));

    this.setStateAndTrigger(
      this.getState().set(
        LIST_SHOWS,
        _sort(
          allShows.filter(
            (show) =>
              show.get('saved') !== true &&
              show.get('template') !== true &&
              !show.get('deleted'),
          ),
        ),
      ),
    );
  },

  // eslint-disable-next-line no-unused-vars
  onPopulateFailed(err) {
    //TODO..
  },

  onDuplicateCompleted(show) {
    this._appendNewShow(show, LIST_SHOWS);
    ToastActions.success('Success');
  },

  onDuplicateFailed() {
    //TODO..
  },

  onCreateSavedVersionCompleted(show) {
    this._appendNewShow(show, LIST_SAVED);
    ToastActions.success('Success');
  },

  // eslint-disable-next-line no-unused-vars
  onCreateSavedVersionFailed(show) {
    //TODO..
  },

  onCreateFromSavedVersionCompleted(show) {
    this._appendNewShow(show, LIST_SHOWS);
    ToastActions.success('Success');
  },

  // eslint-disable-next-line no-unused-vars
  onCreateFromSavedVersionFailed(show) {
    //TODO..
  },

  onCreateFromTemplateCompleted(show) {
    this._appendNewShow(show, LIST_SHOWS);
    ToastActions.success('Success');
  },

  onCreateTemplateCompleted(show) {
    this._appendNewShow(show, LIST_TEMPLATES);
    ToastActions.success('Success');
  },

  onArchive(teamId, eventId, showId) {
    let deletedShow = this.getState().getIn([LIST_SHOWS, showId]);

    this._appendNewShow(deletedShow, LIST_ARCHIVES);
    this._removeShow(deletedShow, LIST_SHOWS);
  },

  _setArchivedTransient(showId, archived) {
    let listName = LIST_TEMPLATES;
    let restoredShow = this.getInState([listName, showId]);

    if (!restoredShow) {
      listName = LIST_SAVED;
      restoredShow = this.getInState([listName, showId]);
    }

    if (!restoredShow) return;

    this._removeShow(restoredShow, listName);
    this._appendNewShow(restoredShow.set('archived', archived), listName);
  },

  onArchiveTemplate(teamId, eventId, showId) {
    this._setArchivedTransient(showId, true);
  },

  onRestoreTemplate(teamId, eventId, showId) {
    this._setArchivedTransient(showId, false);
  },

  onArchiveTemplateCompleted() {
    ToastActions.success('Success');
  },

  onRestoreTemplateCompleted() {
    ToastActions.success('Success');
  },

  onArchiveCompleted() {
    ToastActions.success('Success');
  },

  onSoftDeleteSaved(teamId, eventId, showId) {
    let deletedShow = this.getState().getIn([LIST_SAVED, showId]);

    this._appendNewShow(deletedShow, LIST_ARCHIVES);
    this._removeShow(deletedShow, LIST_SAVED);
  },

  onSoftDeleteSavedCompleted() {
    ToastActions.success('Success');
  },

  onSoftDeleteFailed() {
    //TODO..
  },

  onSoftDeleteTemplate(teamId, eventId, showId) {
    let deletedShow = this.getState().getIn([LIST_TEMPLATES, showId]);

    this._appendNewShow(deletedShow, LIST_ARCHIVES);
    this._removeShow(deletedShow, LIST_TEMPLATES);
  },

  onSoftDeleteSavedTemplate() {
    ToastActions.success('Success');
  },

  onRestoreCompleted(show) {
    let targetList = LIST_SHOWS;

    if (show.saved) {
      targetList = LIST_SAVED;
    } else if (show.template) {
      targetList = LIST_TEMPLATES;
    }

    let restoredShow = Map(show);

    this._appendNewShow(restoredShow, targetList);
    this._removeShow(restoredShow, LIST_ARCHIVES);

    ToastActions.success('Success');
  },

  _updateShow(showId, changes, listKey) {
    let key = listKey || LIST_SHOWS;

    let oldShow = this.getInState([key, showId]);

    if (!oldShow) return;

    let updatedShow = oldShow.merge(changes);

    let nextState = this.setInState([key, showId], updatedShow);
    this.setInStateAndTrigger(nextState);
  },

  _appendNewShow(show, listKey) {
    show = Map.isMap(show) ? show : Map(show);

    let nextState = this.getState().setIn(
      [listKey || LIST_SHOWS, show.get('_id')],
      show,
    );
    nextState = nextState.update(listKey || LIST_SHOWS, (shows) =>
      _sort(shows),
    );
    this.setStateAndTrigger(nextState);
  },

  _removeShow(show, listKey) {
    show = Map.isMap(show) ? show : Map(show);

    this.setStateAndTrigger(
      this.getState().deleteIn([listKey || LIST_SHOWS, show.get('_id')]),
    );
  },
});

function _sort(showList) {
  //TODO: Move sorting to a mixin
  return showList.sort((a, b) => {
    if (a.get('startOn') < b.get('startOn')) return -1;
    if (a.get('startOn') > b.get('startOn')) return 1;
    return 0;
  });
}
